import { useCore } from '@/composables/core'
import { clone, filter, is, __ } from 'ramda'
import moment from 'moment'

import {
  DEV_CONSOLE_TYPE_TEXT as TYPE_TEXT,
  DEV_CONSOLE_TYPE_ERROR as TYPE_ERROR,
  DEV_CONSOLE_TYPE_REQUEST as TYPE_REQUEST,
  DEV_CONSOLE_TYPE_ROUTE_CHANGE as TYPE_ROUTE_CHANGE,
} from '@/lib/consts.js'

export const BUFFER_SIZE = 100

type LogEntry = {
  type: symbol;
  time: string;
  text?: string;
  rest?: unknown[];
  method?: string;
  url?: string;
  params?: Record<string, unknown>;
  from?: string;
  to?: string;
};

const BUFFER: LogEntry[] = []

const isObject = is(Object as unknown, __)

const logEntry = (entry: Omit<LogEntry, 'time'>): void => {
  const time = moment().format('HH:mm:ss')
  BUFFER.unshift({
    ...entry,
    time,
  })
  BUFFER.length = BUFFER_SIZE
}

const logText = (text: string, ...rest: unknown[]): void => {
  logEntry({
    type: TYPE_TEXT,
    text,
    rest,
  })
}

const logError = (text: string, ...rest: unknown[]): void => {
  logEntry({
    type: TYPE_ERROR,
    text,
    rest,
  })
}

interface RequestParams {
  method: string;
  url: string;
  params: Record<string, unknown>;
}

const logRequest = ({ method, url, params }: RequestParams): void => {
  logEntry({
    type: TYPE_REQUEST,
    method,
    url,
    params,
  })
}

interface RouteLike {
  fullPath: string;
}

const logRouteChange = (to: RouteLike, from: RouteLike): void => {
  logEntry({
    type: TYPE_ROUTE_CHANGE,
    from: from.fullPath,
    to: to.fullPath,
  })
}

interface AppState {
  app: { mode: string; basePath: string };
}

const toggleAppMode = (): void => {
  const { store } = useCore() as { store: { state: AppState } }
  const mode = store.state.app.mode === 'production' ? 'development' : 'production'
  localStorage.setItem('app.mode', mode)
  window.location.reload()
}

const getStartUrl = (): string => {
  const { store } = useCore() as { store: { state: AppState } }
  return [window.location.origin, store.state.app.basePath, '/start.html'].join(
    ''
  )
}

const getBuffer = (): LogEntry[] => {
  return clone(filter(isObject, BUFFER)) as LogEntry[]
}

export interface UseDevConsoleReturn {
  logText: (text: string, ...rest: unknown[]) => void;
  logError: (text: string, ...rest: unknown[]) => void;
  logRequest: (params: RequestParams) => void;
  logRouteChange: (to: RouteLike, from: RouteLike) => void;
  toggleAppMode: () => void;
  getBuffer: () => LogEntry[];
  getStartUrl: () => string;
}

export const useDevConsole = (): UseDevConsoleReturn => {
  return {
    logText,
    logError,
    logRequest,
    logRouteChange,
    toggleAppMode,
    getBuffer,
    getStartUrl,
  }
}
